import { useState, useEffect } from 'react';
import { createBusinessInvite } from '../../services/businessService';
import { searchClients } from '../../services/clientService';
import { searchProperties } from '../../services/propertyService';
import type { Client } from '../../types/client';
import type { Property } from '../../types/property';

interface InviteTeamOwnerModalProps {
  onClose: () => void;
  onSuccess: () => void;
  clientId?: string;
}

export function InviteTeamOwnerModal({ onClose, onSuccess, clientId: fixedClientId }: InviteTeamOwnerModalProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState(fixedClientId || '');
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertyId, setPropertyId] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [startingSeats, setStartingSeats] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    searchClients({}, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setClients);
  }, []);

  // Load properties when clientId changes
  useEffect(() => {
    const cId = clientId;
    if (!cId) { setProperties([]); setPropertyId(''); return; }
    searchProperties({ clientId: cId }, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setProperties);
    setPropertyId('');
  }, [clientId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !displayName.trim() || !teamName.trim()) return;

    setError('');
    setLoading(true);
    try {
      await createBusinessInvite({
        email: email.trim(),
        displayName: displayName.trim(),
        teamName: teamName.trim(),
        startingSeats,
        demo: false,
        ...(clientId ? { clientId } : {}),
        ...(propertyId ? { propertyId } : {}),
      });
      onSuccess();
    } catch {
      setError('Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">New Team / Invite Owner</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Client (optional)</label>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  disabled={!!fixedClientId}
                  className={`${inputClass}${fixedClientId ? ' bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">None</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              {clientId && (
                <div>
                  <label className={labelClass}>Property (optional)</label>
                  <select
                    value={propertyId}
                    onChange={(e) => setPropertyId(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All Properties</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className={labelClass}>Owner Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={inputClass}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className={labelClass}>Owner Display Name *</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  className={inputClass}
                  placeholder="Display name"
                />
              </div>
              <div>
                <label className={labelClass}>Team Name *</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  required
                  className={inputClass}
                  placeholder="Team name"
                />
              </div>
              <div>
                <label className={labelClass}>Starting Seats</label>
                <input
                  type="number"
                  value={startingSeats}
                  onChange={(e) => setStartingSeats(Number(e.target.value))}
                  min={0}
                  required
                  className={inputClass}
                />
              </div>
            </div>
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !email.trim() || !displayName.trim() || !teamName.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Inviting...' : 'New Team / Invite Owner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
